import type { Game, Opponent, Player, Season, Snapshot, StatEvent, ID } from '../domain/types'
import type { Repo } from './repo'

const PREFIX = 'hustle:'

/**
 * Fallback store for the case where IndexedDB is unavailable or hangs -- which on
 * old WebKit builds does happen, and mid-game is the worst possible moment to
 * find out. Each collection is one JSON blob; a full season of three stats across
 * ~20 games is on the order of 100-200KB, comfortably inside the ~5MB quota.
 */
export class LocalRepo implements Repo {
  readonly kind = 'localstorage' as const

  async init(): Promise<void> {
    // Probe an actual write: Safari private mode reports localStorage as present
    // but throws QuotaExceededError on the first setItem.
    const probe = PREFIX + '__probe'
    localStorage.setItem(probe, '1')
    localStorage.removeItem(probe)
  }

  private read<T>(key: string, fallback: T): T {
    const raw = localStorage.getItem(PREFIX + key)
    if (raw == null) return fallback
    try {
      return JSON.parse(raw) as T
    } catch {
      return fallback
    }
  }

  private write(key: string, value: unknown): void {
    localStorage.setItem(PREFIX + key, JSON.stringify(value))
  }

  private async upsert<T extends { id: ID }>(key: string, row: T): Promise<void> {
    const rows = this.read<T[]>(key, [])
    const i = rows.findIndex((r) => r.id === row.id)
    if (i >= 0) rows[i] = row
    else rows.push(row)
    this.write(key, rows)
  }

  async listPlayers() {
    return this.read<Player[]>('players', [])
  }
  savePlayer(p: Player) {
    return this.upsert('players', p)
  }

  async deletePlayer(id: ID): Promise<void> {
    this.write(
      'players',
      this.read<Player[]>('players', []).filter((p) => p.id !== id),
    )
  }

  async listOpponents() {
    return this.read<Opponent[]>('opponents', [])
  }
  saveOpponent(o: Opponent) {
    return this.upsert('opponents', o)
  }

  async listSeasons() {
    return this.read<Season[]>('seasons', [])
  }
  saveSeason(s: Season) {
    return this.upsert('seasons', s)
  }

  async listGames() {
    return this.read<Game[]>('games', [])
  }
  saveGame(g: Game) {
    return this.upsert('games', g)
  }

  async getGame(id: ID) {
    return this.read<Game[]>('games', []).find((g) => g.id === id)
  }

  async deleteGame(id: ID): Promise<void> {
    this.write(
      'games',
      this.read<Game[]>('games', []).filter((g) => g.id !== id),
    )
    this.write(
      'events',
      this.read<StatEvent[]>('events', []).filter((e) => e.gameId !== id),
    )
  }

  async listEventsByGame(gameId: ID) {
    return this.read<StatEvent[]>('events', []).filter((e) => e.gameId === gameId)
  }

  async listAllEvents() {
    return this.read<StatEvent[]>('events', [])
  }

  saveEvent(e: StatEvent) {
    return this.upsert('events', e)
  }

  async saveEvents(events: StatEvent[]): Promise<void> {
    if (events.length === 0) return
    const rows = this.read<StatEvent[]>('events', [])
    const byId = new Map(rows.map((r) => [r.id, r]))
    for (const e of events) byId.set(e.id, e)
    this.write('events', [...byId.values()])
  }

  async getMeta<T>(key: string): Promise<T | undefined> {
    const meta = this.read<Record<string, unknown>>('meta', {})
    return meta[key] as T | undefined
  }

  async setMeta<T>(key: string, value: T): Promise<void> {
    const meta = this.read<Record<string, unknown>>('meta', {})
    meta[key] = value
    this.write('meta', meta)
  }

  async exportAll(): Promise<Snapshot> {
    return {
      version: 1,
      exportedAt: Date.now(),
      players: await this.listPlayers(),
      opponents: await this.listOpponents(),
      seasons: await this.listSeasons(),
      games: await this.listGames(),
      events: await this.listAllEvents(),
    }
  }

  async importAll(snapshot: Snapshot): Promise<void> {
    const merge = <T extends { id: ID }>(key: string, incoming: T[]) => {
      const byId = new Map(this.read<T[]>(key, []).map((r) => [r.id, r]))
      for (const row of incoming) byId.set(row.id, row)
      this.write(key, [...byId.values()])
    }
    merge('players', snapshot.players)
    merge('opponents', snapshot.opponents)
    merge('seasons', snapshot.seasons)
    merge('games', snapshot.games)
    merge('events', snapshot.events)
  }

  async clearAll(): Promise<void> {
    for (const key of ['players', 'opponents', 'seasons', 'games', 'events', 'meta']) {
      localStorage.removeItem(PREFIX + key)
    }
  }
}
